import { ReactNode, useContext, useEffect, useState } from "react";
import { ChevronRight, XIcon } from "lucide-react";

import { Button } from "components/ui/button";
import { MqttClientContext } from "services/mqtt-client";
import { Topics } from "services/topics";
import { DEV_TOOLS_ENABLED, SIMULATE } from "utils/constants";
import {
  criticalAlert,
  externalAlert,
  freshWeatherData,
  geofenceAlert,
  infoAlert,
  mockCompanySubscription,
  mockConfig,
  mockEcoSog,
  mockSystemStatus,
  mockWeatherData,
  thresholdAlert,
  thresholdAlert2,
  warningAlert,
} from "services/simulation/data";
import {
  simulateRespones,
  simulateTrip,
  SimulationMode,
} from "services/simulation/simulate";
import Toggle from "components/ui/toggle";
import { StoreState, useStore } from "hooks/use-store";
import { Select } from "components/ui/select";
import { EcoSog, EcoSogState } from "types/eco-sog";
import { GuidanceMode } from "types/enums";
import { WeatherData } from "types/weather";

const ecoSogStateOptions = [
  { text: "OK", value: "ok" },
  { text: "No model", value: "no_model" },
  { text: "Not enough data", value: "not_enough_data" },
  { text: "No fuel rate data", value: "no_fuel_rate_data" },
  { text: "Not calculated", value: "not_calculated" },
];

const featureFlags: {
  label: string;
  featureKey: string;
  enabled: (flags: StoreState["featureFlags"]) => boolean;
}[] = [
  {
    label: "Safety Speed",
    featureKey: "safety-speed",
    enabled: (flags) => flags.safetySpeed,
  },
  {
    label: "Eco Speed",
    featureKey: "eco-speed",
    enabled: (flags) => flags.ecoSpeed,
  },
  {
    label: "Sea Sickness",
    featureKey: "sea-sickness",
    enabled: (flags) => flags.seaSickness ?? false,
  },
];

const alertButtons = [
  // Auto-dismissing toasts per severity (critical = full-screen flash)
  { label: "Critical", alert: criticalAlert },
  { label: "Warning", alert: warningAlert },
  { label: "Info", alert: infoAlert },
  // Full-screen toasts that require acknowledgement
  { label: "Critical + ack", alert: thresholdAlert2 },
  { label: "Warning + ack", alert: thresholdAlert },
  { label: "Info + ack", alert: geofenceAlert },
  // External alert without a rule (severity defaults to critical)
  { label: "N2K", alert: externalAlert },
];

export const DevTools = ({ show = true }) => {
  const [isSpeedSimulated, setIsSpeedSimulated] = useState(false);
  const [isImpactSimulated, setIsImpactSimulated] = useState(false);
  const [isEcoSpeedSimulated, setIsEcoSpeedSimulated] = useState(false);

  const store = useStore();
  // Without simulation the panel stays hidden until opened with Cmd/Ctrl+D
  // or the Developer Tools button in the settings screen
  const showPanel = store.showDevTools;
  const setShowPanel = store.setShowDevTools;

  // MQTT client
  const client = useContext(MqttClientContext);

  useEffect(() => {
    if (!SIMULATE || !client?.connected) return;

    const clearListeners = simulateRespones(client);
    const clearDataIntervals = simulateTrip(client, {
      simulateSpeed: isSpeedSimulated,
      simulateImpacts: isImpactSimulated,
      simulateEcoSpeed: isEcoSpeedSimulated,
      mode: SimulationMode.PingPong,
    });

    return () => {
      clearDataIntervals();
      clearListeners();
    };
  }, [
    client?.connected,
    isSpeedSimulated,
    isImpactSimulated,
    isEcoSpeedSimulated,
  ]);

  // Toggle the panel with Cmd/Ctrl+D on test builds and in local development
  useEffect(() => {
    if (!DEV_TOOLS_ENABLED) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "d") {
        e.preventDefault();
        const { showDevTools, setShowDevTools } = useStore.getState();
        setShowDevTools(!showDevTools);
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // Simulate data based on input
  useEffect(() => {
    if (!SIMULATE || !client?.connected || isSpeedSimulated) return;

    let speed = 15;
    let impact = 0;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "w" || e.key === "ArrowUp") {
        speed = Math.min(speed + 1, 60);
        client.publish(Topics.Sog, speed.toString());
      } else if (e.key === "s" || e.key === "ArrowDown") {
        speed = Math.max(speed - 1, 0);
        client.publish(Topics.Sog, speed.toString());
      } else if (e.key === "q") {
        impact = Math.min(impact + 0.2, 10);
        client.publish(Topics.Impact, impact.toString());
      } else if (e.key === "a") {
        impact = Math.max(impact - 0.2, 0);
        client.publish(Topics.Impact, impact.toString());
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [client?.connected, isSpeedSimulated]);

  if (!DEV_TOOLS_ENABLED || !client?.connected || !show) return null;

  const setFeatureFlag = (featureKey: string, enabled: boolean) => {
    const features = {
      ...store.config?.company_subscription?.features_transformed,
    };
    if (enabled) {
      features[featureKey] = "{}";
    } else {
      delete features[featureKey];
    }
    store.setConfig({
      ...mockConfig,
      company_subscription: {
        ...mockCompanySubscription,
        features_transformed: features,
      },
    });
  };

  const publishEcoSog = (field: keyof EcoSog, value: number | null) => {
    client.publish(
      Topics.EcoSog,
      JSON.stringify({
        ...(store.ecoSog ?? mockEcoSog),
        [field]: value,
      })
    );
  };

  const publishWeather = (field: keyof WeatherData, value: number | null) => {
    client.publish(
      Topics.WeatherData,
      JSON.stringify({
        ...freshWeatherData(store.weatherData ?? mockWeatherData),
        [field]: value,
      })
    );
  };

  if (!showPanel) {
    return null;
  }

  return (
    <div className="h-screen pb-20 w-[350px] border-l border-primary-700 divide-y bg-primary-800 divide-primary-700 overflow-auto">
      <div className="px-3 py-3 text-center flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium">Developer Tools</h2>
        <button
          onClick={() => setShowPanel(false)}
          className="p-1 rounded hover:bg-primary-800"
        >
          <XIcon className="size-4" />
        </button>
      </div>
      <Collapsible title="Configuration">
        <Row label="Pairing Request">
          <Toggle
            enabled={store.pairingRequest}
            setEnabled={(value) => {
              client.publish(Topics.PairingRequest, value ? "true" : "false");
            }}
            size="sm"
          />
        </Row>
        <Row label="Eco speed enabled">
          <Toggle
            enabled={store.hasEcoSog}
            setEnabled={(value) => {
              client.publish(
                Topics.HasEcoSog,
                JSON.stringify({ enabled: value ? true : false })
              );
            }}
            size="sm"
          />
        </Row>
        {store.hasEcoSog && (
          <Row label="Eco speed state">
            <Select
              variant={"secondary"}
              size="xs"
              triggerClassName="w-fit"
              options={ecoSogStateOptions}
              selected={store.ecoSog?.fuel_curve_stats?.state || "no_model"}
              setSelected={(option) => {
                let newEcoSog: EcoSog | null = {
                  ...mockEcoSog,
                  fuel_curve_stats: {
                    ...mockEcoSog.fuel_curve_stats,
                    state: option.value as EcoSogState,
                  },
                };

                if (option.value === "no_model") {
                  newEcoSog = null;
                }

                client.publish(Topics.EcoSog, JSON.stringify(newEcoSog));
              }}
            />
          </Row>
        )}
      </Collapsible>
      <Collapsible title="Feature Flags">
        {featureFlags.map(({ label, featureKey, enabled }) => (
          <Row key={featureKey} label={label}>
            <Toggle
              enabled={enabled(store.featureFlags)}
              setEnabled={(value) => setFeatureFlag(featureKey, value)}
              size="sm"
            />
          </Row>
        ))}
      </Collapsible>
      <Collapsible title="Fetching">
        <div className="grid grid-cols-2 gap-2">
          <Button
            className="w-full"
            variant="secondary"
            size="xs"
            onClick={() => {
              store.setConfig({
                ...mockConfig,
                guidance_mode: GuidanceMode.Eco,
              });
            }}
          >
            Config
          </Button>
          <Button
            className="w-full"
            variant="secondary"
            size="xs"
            onClick={() => {
              client.publish(
                Topics.SystemStatus,
                JSON.stringify(mockSystemStatus)
              );
            }}
          >
            System status
          </Button>
          <Button
            className="w-full"
            variant="secondary"
            size="xs"
            onClick={() => {
              client.publish(
                Topics.WeatherData,
                JSON.stringify(freshWeatherData())
              );
            }}
          >
            Weather
          </Button>
          <Button
            className="w-full"
            variant="secondary"
            size="xs"
            onClick={() => {
              client.publish(Topics.EcoSog, JSON.stringify(mockEcoSog));
            }}
          >
            Eco SOG
          </Button>
        </div>
      </Collapsible>
      <Collapsible title="Alerts">
        <div className="grid grid-cols-2 gap-2">
          {alertButtons.map(({ label, alert }) => (
            <Button
              key={label}
              className="w-full"
              variant="secondary"
              size="xs"
              onClick={() => {
                client.publish(Topics.Alert, JSON.stringify(alert));
              }}
            >
              {label}
            </Button>
          ))}
        </div>
      </Collapsible>
      <Collapsible title="Simulation">
        <Row label="SOG">
          <Toggle
            enabled={isSpeedSimulated}
            setEnabled={setIsSpeedSimulated}
            size="sm"
          />
        </Row>
        <Row label="Impact">
          <Toggle
            enabled={isImpactSimulated}
            setEnabled={setIsImpactSimulated}
            size="sm"
          />
        </Row>
        <Row label="Eco Speed">
          <Toggle
            enabled={isEcoSpeedSimulated}
            setEnabled={setIsEcoSpeedSimulated}
            size="sm"
          />
        </Row>
      </Collapsible>
      <Collapsible title="Sliders">
        <ToggleableSlider
          label="SOG"
          min={0}
          max={100}
          step={1}
          value={store.sog}
          publish={(value) => {
            client.publish(Topics.Sog, value);
          }}
        />
        <ToggleableSlider
          label="Impact"
          min={0}
          max={10}
          step={0.1}
          value={store.impact}
          publish={(value) => {
            client.publish(Topics.Impact, value);
          }}
        />
        <ToggleableSlider
          label="Peak Impact"
          min={0}
          max={10}
          step={0.1}
          value={store.peakImpact}
          publish={(value) => {
            client.publish(Topics.PeakImpact, value);
          }}
        />
        <ToggleableSlider
          label="Red Impacts"
          min={0}
          max={50}
          step={1}
          value={store.redImpacts}
          publish={(value) => {
            client.publish(Topics.RedImpacts, value);
          }}
        />
        <ToggleableSlider
          label="L/nmi"
          min={0}
          max={20}
          step={0.1}
          value={store.ecoSog?.avg_fuel_rate_l_nm?.["30s"] ?? 0}
          publish={(value) => {
            const newEcoSog = {
              ...store.ecoSog,
              avg_fuel_rate_l_nm: {
                ...store.ecoSog?.avg_fuel_rate_l_nm,
                ["30s"]: parseFloat(value),
              },
            };
            client.publish(Topics.EcoSog, JSON.stringify(newEcoSog));
          }}
        />
        <ToggleableSlider
          label="Eco Speed"
          min={0}
          max={60}
          step={1}
          value={store.ecoSog?.eco_speed ?? null}
          publish={(value) => publishEcoSog("eco_speed", parseFloat(value))}
        />
        <ToggleableSlider
          label="Eco RPM"
          min={0}
          max={8000}
          step={100}
          value={store.ecoSog?.eco_rpm ?? null}
          publish={(value) => publishEcoSog("eco_rpm", parseFloat(value))}
        />
        <ToggleableSlider
          label="Fuel Savings"
          min={-20}
          max={20}
          step={0.1}
          value={store.ecoSog?.fuel_deviation_from_pred?.trip ?? null}
          publish={(value) => {
            client.publish(
              Topics.EcoSog,
              JSON.stringify({
                ...(store.ecoSog ?? mockEcoSog),
                fuel_deviation_from_pred: {
                  ...store.ecoSog?.fuel_deviation_from_pred,
                  trip: parseFloat(value),
                },
              })
            );
          }}
        />
        <ToggleableSlider
          label="Warn L/nmi"
          min={0}
          max={20}
          step={0.1}
          value={store.ecoSog?.fuel_rate_warn_threshold ?? null}
          publish={(value) =>
            publishEcoSog("fuel_rate_warn_threshold", parseFloat(value))
          }
        />
        <ToggleableSlider
          label="SQ"
          min={0}
          max={1}
          step={0.01}
          value={store.safetyQuality}
          publish={(value) => {
            client.publish(Topics.SafetyQuality, value);
          }}
        />
        <ToggleableSlider
          label="Safety Speed"
          min={0}
          max={100}
          step={1}
          value={store.rog}
          publish={(value) => {
            client.publish(Topics.Rog, value);
          }}
        />
        <ToggleableSlider
          label="RPM"
          min={0}
          max={8000}
          step={100}
          value={store.rpm}
          publish={(value) => {
            client.publish(Topics.Rpm, value);
          }}
        />
        <ToggleableSlider
          label="Sea Sickness"
          min={0}
          max={100}
          step={1}
          value={
            store?.wholeBodyVibration?.["undefined"]?.motion_sickness_index
              ?.value ?? 0
          }
          publish={(value) => {
            const newWholeBodyVibration = {
              ...store.wholeBodyVibration["undefined"],
              motion_sickness_index: {
                value: parseInt(value),
              },
            };
            client.publish(
              Topics.WholeBodyVibration,
              JSON.stringify(newWholeBodyVibration)
            );
          }}
        />
        <ToggleableSlider
          label="Sea State"
          min={0}
          max={12}
          step={1}
          value={store.weatherData?.sea_state ?? null}
          publish={(value) => publishWeather("sea_state", parseFloat(value))}
        />
        <ToggleableSlider
          label="Wind m/s"
          min={0}
          max={40}
          step={0.5}
          value={store.weatherData?.wind_speed ?? null}
          publish={(value) => publishWeather("wind_speed", parseFloat(value))}
        />
        <ToggleableSlider
          label="Wind Dir"
          min={0}
          max={360}
          step={5}
          value={store.weatherData?.wind_direction ?? null}
          publish={(value) =>
            publishWeather("wind_direction", parseFloat(value))
          }
        />
        <ToggleableSlider
          label="Air °C"
          min={-20}
          max={40}
          step={0.5}
          value={store.weatherData?.air_temperature ?? null}
          publish={(value) =>
            publishWeather("air_temperature", parseFloat(value))
          }
        />
        <ToggleableSlider
          label="Distance"
          min={0}
          max={100}
          step={0.5}
          value={store.distance}
          publish={(value) => {
            client.publish(Topics.Distance, value);
          }}
        />
        <ToggleableSlider
          label="Duration"
          min={0}
          max={1440}
          step={10}
          // The store holds duration formatted as "HH:MM", so the slider
          // falls back to its last dragged value (unit is minutes)
          value={store.duration}
          publish={(value) => {
            client.publish(Topics.Duration, value);
          }}
        />
      </Collapsible>
    </div>
  );
};

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex items-center justify-between gap-3 py-1 px-1">
    <p className="text-sm">{label}</p>
    {children}
  </div>
);

interface ToggleableSliderProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number | string | null;
  publish: (value: string) => void;
}

const ToggleableSlider = ({
  label,
  min,
  max,
  step,
  value,
  publish,
}: ToggleableSliderProps) => {
  // Fallback for values the store holds in a non-numeric format (e.g.
  // duration as "HH:MM"), so the thumb doesn't snap back after dragging
  const [fallbackValue, setFallbackValue] = useState(0);
  const safeValue = Number.isFinite(Number(value))
    ? Number(value)
    : fallbackValue;

  return (
    <div className="flex items-center justify-between gap-2 py-1 px-1">
      <p className="flex-1 text-sm">{label}</p>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        className="flex-1"
        disabled={false}
        value={safeValue}
        onChange={(e) => {
          setFallbackValue(Number(e.target.value));
          publish(e.target.value);
        }}
      />
      <span className="tabular-nums text-[10px] text-left w-12">
        {safeValue.toFixed(1)}
      </span>
    </div>
  );
};

const Collapsible = ({ defaultOpen = false, children, title }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        aria-label="collapsible"
        onClick={() => setOpen((prev) => !prev)}
        className="group flex justify-between items-center w-full p-3"
      >
        <h3 className={"text-primary-400 text-sm"}>{title}</h3>
        <ChevronRight
          className={`${
            open ? "rotate-90" : ""
          } transition-all size-4 text-primary-300 group-hover:text-white`}
        />
      </button>
      {open && <div className="px-3 pb-2">{children}</div>}
    </div>
  );
};
